import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { EyeIcon, EyeOffIcon, AlertCircleIcon, KeyIcon, UserIcon, LockIcon, MailIcon, ShieldIcon } from 'lucide-react';
const Login: React.FC = () => {
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    login
  } = useAuth();
  const navigate = useNavigate();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const success = await login(usernameOrEmail, password);
      if (success) {
        navigate('/dashboard');
      } else {
        setError('Invalid username/email or password');
      }
    } catch (err) {
      setError('An error occurred during login');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen flex flex-col md:flex-row bg-white dark:bg-gray-900" data-id="element-1688">
      {/* Left side - Branding/Illustration */}
      <div className="hidden md:flex md:w-1/2 bg-blue-600 text-white flex-col justify-center items-center p-12" data-id="element-1689">
        <div className="max-w-md mx-auto" data-id="element-1690">
          <div className="text-center" data-id="element-1691">
            <h1 className="text-3xl font-bold mb-2" data-id="element-1692">Merchant Admin Portal</h1>
            <p className="text-blue-100 mb-8" data-id="element-1693">
              Secure Merchant Management System for financial institutions
            </p>
          </div>
          <div className="bg-blue-700/30 rounded-lg p-6 backdrop-blur-sm" data-id="element-1694">
            <h2 className="text-xl font-semibold mb-4" data-id="element-1695">System Features</h2>
            <ul className="space-y-2" data-id="element-1696">
              <li className="flex items-start" data-id="element-1697">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-500/50 flex items-center justify-center mr-3 mt-0.5" data-id="element-1698">
                  <span className="text-sm font-bold" data-id="element-1699">1</span>
                </div>
                <span data-id="element-1700">Complete merchant onboarding & KYC</span>
              </li>
              <li className="flex items-start" data-id="element-1701">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-500/50 flex items-center justify-center mr-3 mt-0.5" data-id="element-1702">
                  <span className="text-sm font-bold" data-id="element-1703">2</span>
                </div>
                <span data-id="element-1704">EMVCo compliant QR code generation</span>
              </li>
              <li className="flex items-start" data-id="element-1705">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-500/50 flex items-center justify-center mr-3 mt-0.5" data-id="element-1706">
                  <span className="text-sm font-bold" data-id="element-1707">3</span>
                </div>
                <span data-id="element-1708">Transaction monitoring & dispute management</span>
              </li>
              <li className="flex items-start" data-id="element-1709">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-500/50 flex items-center justify-center mr-3 mt-0.5" data-id="element-1710">
                  <span className="text-sm font-bold" data-id="element-1711">4</span>
                </div>
                <span data-id="element-1712">Advanced reporting & analytics</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex flex-col justify-center items-center p-6 md:p-12 md:w-1/2" data-id="element-1713">
        <div className="w-full max-w-md" data-id="element-1714">
          <div className="text-center mb-10" data-id="element-1715">
            <div className="md:hidden" data-id="element-1716">
              <img src="https://cdn.magicpatterns.com/uploads/vs2Gt4T5WiifLNuTU8evPc/Paysys_Logo.png" alt="Paysys" className="h-10 mx-auto mb-4" data-id="element-1717" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-1718">
                Paysys Admin Portal
              </h1>
              <p className="text-gray-600 dark:text-gray-400" data-id="element-1719">
                Sign in to your account
              </p>
            </div>
            <div className="hidden md:block" data-id="element-1720">
              <img src="https://cdn.magicpatterns.com/uploads/vs2Gt4T5WiifLNuTU8evPc/Paysys_Logo.png" alt="Paysys" className="h-12 mx-auto mb-12" data-id="element-1721" />
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white" data-id="element-1722">
                Welcome
              </h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400" data-id="element-1723">
                Please sign in to your account
              </p>
            </div>
          </div>
          {error && <div className="mb-6 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-4 py-3 rounded-md flex items-center" data-id="element-1724">
              <AlertCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" data-id="element-1725" />
              <span data-id="element-1726">{error}</span>
            </div>}
          <form className="space-y-6" onSubmit={handleSubmit} data-id="element-1727">
            <div data-id="element-1728">
              <label htmlFor="username-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1729">
                Username or Email
              </label>
              <div className="relative" data-id="element-1730">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1731">
                  <MailIcon className="h-5 w-5 text-gray-400" data-id="element-1732" />
                </div>
                <input id="username-email" name="username-email" type="text" autoComplete="username" required className="appearance-none rounded-md relative block w-full px-3 py-3 pl-10 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white dark:bg-gray-800 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm" placeholder="Enter your username or email" value={usernameOrEmail} onChange={e => setUsernameOrEmail(e.target.value)} data-id="element-1733" />
              </div>
            </div>
            <div data-id="element-1734">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1735">
                Password
              </label>
              <div className="relative" data-id="element-1736">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1737">
                  <LockIcon className="h-5 w-5 text-gray-400" data-id="element-1738" />
                </div>
                <input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" required className="appearance-none rounded-md relative block w-full px-3 py-3 pl-10 pr-10 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white dark:bg-gray-800 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm" placeholder="Enter your password" value={password} onChange={e => setPassword(e.target.value)} data-id="element-1739" />
                <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword(!showPassword)} data-id="element-1740">
                  {showPassword ? <EyeOffIcon className="h-5 w-5 text-gray-400" data-id="element-1741" /> : <EyeIcon className="h-5 w-5 text-gray-400" data-id="element-1742" />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between" data-id="element-1743">
              <div className="flex items-center" data-id="element-1744">
                <input id="remember-me" name="remember-me" type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-1745" />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-1746">
                  Remember me
                </label>
              </div>
              <div className="text-sm" data-id="element-1747">
                <Link to="/forgot-password" className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300" data-id="element-1748">
                  Forgot your password?
                </Link>
              </div>
            </div>
            <div data-id="element-1749">
              <button type="submit" disabled={isLoading} className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-70 transition-colors duration-200" data-id="element-1750">
                {isLoading ? <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1751">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1752"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1753"></path>
                    </svg>
                    Signing in...
                  </> : 'Sign in'}
              </button>
            </div>
          </form>
          <div className="mt-8 text-center" data-id="element-1754">
            <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1755">
              Demo credentials:{' '}
              <span className="font-medium" data-id="element-1756">adminmaker / Test@1234</span>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default Login;